import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';

export interface IDPApiResponse {
  status: number;
  message: string;
  data: {
    resultType: string;
    resultMessage: string;
  };
}

interface RawResponse {
  status?: number;
  message?: string;
  errorMessage?: string;
  data?: {
    resultType?: string;
    resultMessage?: string;
  };
  error?: {
    status?: number;
    message?: string;
    data?: {
      resultType?: string;
      resultMessage?: string;
    };
  };
}

/**
 * Normalizes the HttpService response into a consistent IDPApiResponse.
 *
 * HttpService returns parsed JSON for 200, but for non-200 (including 201)
 * it returns a Response object with the parsed body on `.error`.
 */
export const normalizeIDPResponse = (raw: unknown): IDPApiResponse => {
  const response = raw as RawResponse;

  if (response.data?.resultType) {
    return response as IDPApiResponse;
  }

  const errorBody = response.error;

  if (errorBody?.data?.resultType) {
    return errorBody as IDPApiResponse;
  }

  return {
    status: response.status ?? 500,
    message: response.message ?? '',
    data: {
      resultType: 'UNKNOWN',
      resultMessage:
        errorBody?.data?.resultMessage ?? response.errorMessage ?? '',
    },
  };
};

/**
 * Validates the IDP session by calling the verification endpoint.
 *
 * @async
 * @function verifyIDPSession
 * @returns {Promise<IDPApiResponse>} The session validation result.
 */
const verifyIDPSession = async ({ key }: { key: string }) =>
  normalizeIDPResponse(await ExternalService.getIDPURL(key));

/**
 * Custom hook to validate an IDP session via the verification API.
 *
 * @function useGetIDPURL
 * @returns {import('@tanstack/react-query').UseMutationResult<IDPApiResponse, unknown>}
 */
const useGetIDPURL = () =>
  useMutation({
    mutationFn: (payload: { key: string }) => verifyIDPSession(payload),
  });

export default useGetIDPURL;
